import React, {
  useState,
  useEffect
} from "react";

import {
  useParams,
  useNavigate
} from "react-router-dom";

import {
  Box,
  Grid,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Dialog,
  DialogContent,
  Fade
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import { productApi } from "../services/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const PDP = () => {

  const { id } = useParams();

  const navigate = useNavigate();

  const {
    cart,
    addToCart,
    updateQuantity
  } = useCart();

  const { user } = useAuth();

  const [product, setProduct] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [quantity, setQuantity] =
    useState(1);

  const [openModal, setOpenModal] =
    useState(false);

  const cartItem = cart.find(
    (item) => item._id === product?._id
  );

  // =========================
  // FETCH PRODUCT
  // =========================
  useEffect(() => {

    if (!id) return;

    fetchProduct();

  }, [id]);

  const fetchProduct = async () => {

    try {

      setLoading(true);

      const res =
        await productApi.getById(id);

      setProduct(res.data);

    } catch (err) {

      setError(
        "Failed to fetch product"
      );

    } finally {

      setLoading(false);

    }

  };

  // =========================
  // INITIAL QUANTITY
  // =========================
  useEffect(() => {

    if (product && cartItem) {

      setQuantity(
        cartItem.quantity
      );

    }

  }, [product]);

  // =========================
  // QUANTITY
  // =========================
  const handleQuantityChange = (
    val
  ) => {

    if (
      val > 0 &&
      val <= (product?.stock || 100)
    ) {

      setQuantity(val);

    }

  };

  // =========================
  // ADD TO CART
  // =========================
  const handleAddToCart =
    async () => {

      if (!product) return;

      // LOGIN CHECK
      if (!user) {

        localStorage.setItem(
          "pendingCart",
          JSON.stringify({
            productId: product._id,
            quantity
          })
        );

        navigate("/login");

        return;

      }

      try {

        if (cartItem) {

          await updateQuantity(
            product._id,
            quantity
          );

        } else {

          await addToCart(
            product,
            quantity
          );

        }

        // SUCCESS MODAL
        setOpenModal(true);

      } catch (err) {

        console.error(err);

      }

    };

  // =========================
  // LOADING
  // =========================
  if (loading) {

    return (

      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >

        <CircularProgress
          sx={{
            color: "#8B0000"
          }}
        />

      </Box>

    );

  }

  // =========================
  // ERROR
  // =========================
  if (!product) {

    return (

      <Typography
        textAlign="center"
        mt={10}
      >
        Product not found
      </Typography>

    );

  }

  return (

    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg,#fff8f6 0%, #f7ece7 100%)",
        py: 2.5,
        px: {
          xs: 2,
          md: 5
        }
      }}
    >

      {/* BACK */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/shop")}
        sx={{
          mb: 2,
          color: "#5c0000",
          fontWeight: 700,
          textTransform: "none"
        }}
      >
        Back to Products
      </Button>

      {/* MAIN CARD */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "18px",
          overflow: "hidden",
          background: "#fff",
          border:
            "1px solid rgba(139,0,0,0.08)",
          boxShadow:
            "0 20px 50px rgba(139,0,0,0.08)"
        }}
      >

        <Grid container>

          {/* IMAGE */}
          <Grid
            item
            xs={12}
            md={6}
          >

            <Box
              sx={{
                p: { xs: 2, md: 3 },
                background:
                  "linear-gradient(135deg,#fff7f5,#fff)"
              }}
            >

              <Paper
                elevation={0}
                sx={{
                  borderRadius: "28px",
                  overflow: "hidden",
                  background:
                    "linear-gradient(135deg,#fff,#fff8f6)",
                  border:
                    "1px solid rgba(139,0,0,0.08)",
                  height: {
                    xs: 260,
                    md: 420
                  },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 2
                }}
              >

                <img
                  src={product.image}
                  alt={product.name}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit:
                      "contain"
                  }}
                />

              </Paper>

            </Box>

          </Grid>

          {/* CONTENT */}
          <Grid
            item
            xs={12}
            md={6}
          >

            <Box
              sx={{
                p: { xs: 2, md: 3 }
              }}
            >

              {/* CATEGORY */}
              <Typography
                sx={{
                  color: "#8B0000",
                  fontWeight: 700,
                  letterSpacing: 1,
                  mb: 0.6,
                  textTransform:
                    "uppercase",
                  fontSize: 12
                }}
              >
                {product.category}
              </Typography>

              {/* NAME */}
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  color: "#2b2b2b",
                  mb: 2,
                  lineHeight: 1.2
                }}
              >
                {product.name}
              </Typography>

              {/* PRICE */}
              <Typography
                variant="h3"
                sx={{
                  color: "#5c0000",
                  fontWeight: 800,
                  mb: 1
                }}
              >
                ₹
                {Number(
                  product.price
                ).toFixed(2)}
              </Typography>

              <Typography
                sx={{
                  color: "#777",
                  mb: 4
                }}
              >
                Inclusive of all taxes
              </Typography>

              {/* DESCRIPTION */}
              <Box
                sx={{
                  background:
                    "#fff8f6",
                  borderRadius:
                    "20px",
                  p: 3,
                  mb: 2,
                  border:
                    "1px solid rgba(139,0,0,0.08)"
                }}
              >

                <Typography
                  variant="h6"
                  fontWeight={700}
                  mb={2}
                  color="#5c0000"
                >
                  Product Description
                </Typography>

                <Typography
                  sx={{
                    lineHeight: 1.8,
                    color: "#555"
                  }}
                >
                  {product.description ||
                    "Premium quality authentic Indian spices crafted with purity and rich taste."}
                </Typography>

              </Box>

              {/* QUANTITY */}
              <Typography
                fontWeight={700}
                mb={2}
                color="#5c0000"
              >
                Select Quantity
              </Typography>

              <Box
                display="flex"
                alignItems="center"
                gap={2}
                mb={5}
              >

                <Button
                  onClick={() =>
                    handleQuantityChange(
                      quantity - 1
                    )
                  }
                  sx={{
                    minWidth: 55,
                    height: 55,
                    borderRadius:
                      "16px",
                    background:
                      "#fff8f6",
                    border:
                      "1px solid rgba(139,0,0,0.1)",
                    color: "#8B0000"
                  }}
                >
                  <RemoveIcon />
                </Button>

                <Box sx={{ width: 100, height: 55, border: "1px solid rgba(139,0,0,0.2)", borderRadius: "16px", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 20 }}>{quantity}</Box>

                <Button
                  onClick={() =>
                    handleQuantityChange(
                      quantity + 1
                    )
                  }
                  sx={{
                    minWidth: 55,
                    height: 55,
                    borderRadius:
                      "16px",
                    background:
                      "#fff8f6",
                    border:
                      "1px solid rgba(139,0,0,0.1)",
                    color: "#8B0000"
                  }}
                >
                  <AddIcon />
                </Button>

              </Box>

              {/* ADD TO CART */}
              <Button
                fullWidth
                variant="contained"
                onClick={
                  handleAddToCart
                }
                startIcon={
                  <ShoppingCartIcon />
                }
                sx={{
                  py: 2,
                  borderRadius:
                    "18px",
                  fontSize: 17,
                  fontWeight: 700,
                  textTransform:
                    "none",
                  background:
                    "linear-gradient(135deg,#5c0000,#8B0000)",
                  boxShadow:
                    "0 12px 30px rgba(139,0,0,0.22)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg,#470000,#700000)"
                  }
                }}
              >
                {cartItem
                  ? "Update Cart"
                  : "Add to Cart"}
              </Button>

            </Box>

          </Grid>

        </Grid>

      </Paper>

      {/* SUCCESS MODAL */}
      <Dialog
        open={openModal}
        onClose={() =>
          setOpenModal(false)
        }
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            borderRadius: "28px",
            p: 3,
            textAlign: "center",
            maxWidth: 420
          }
        }}
      >

        <DialogContent>

          <CheckCircleIcon
            sx={{
              fontSize: 90,
              color: "#16a34a",
              mb: 2
            }}
          />

          <Typography
            variant="h5"
            fontWeight={800}
            mb={1}
          >
            Cart Updated
          </Typography>

          <Typography
            color="text.secondary"
            mb={4}
          >
            Your product has been
            successfully added to cart.
          </Typography>

          <Box
            display="flex"
            gap={2}
          >

            <Button
              fullWidth
              variant="outlined"
              onClick={() =>
                setOpenModal(false)
              }
              sx={{
                borderRadius:
                  "14px",
                borderColor:
                  "#8B0000",
                color: "#8B0000",
                fontWeight: 700
              }}
            >
              Continue Shopping
            </Button>

            <Button
              fullWidth
              variant="contained"
              onClick={() =>
                navigate("/cart")
              }
              sx={{
                borderRadius:
                  "14px",
                fontWeight: 700,
                background:
                  "linear-gradient(135deg,#5c0000,#8B0000)"
              }}
            >
              View Cart
            </Button>

          </Box>

        </DialogContent>

      </Dialog>

    </Box>

  );

};

export default PDP;